import { create } from 'zustand';
import axios from 'axios';
import { User, Organization } from '../types';
import { useFilterStore } from './useFilterStore';

interface AuthState {
  user: User | null;
  token: string | null;
  org: Organization | null;
  allOrgs: Organization[];
  viewAsUser: boolean;
  // Impersonation
  originalToken: string | null;
  originalUser: User | null;
  isImpersonating: boolean;
  setAuth: (user: User, token: string, org?: Organization | null, allOrgs?: Organization[]) => void;
  setToken: (token: string) => void;
  setOrg: (org: Organization, token: string) => void;
  updateUser: (patch: Partial<User>) => void;
  logout: () => void;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  toggleViewAsUser: () => void;
  startImpersonation: (user: User, token: string) => void;
  exitImpersonation: () => void;
}

const stored = localStorage.getItem('erm-user');
const storedOrg = localStorage.getItem('erm-org');
const storedAllOrgs = localStorage.getItem('erm-all-orgs');

export const useAuthStore = create<AuthState>((set, get) => ({
  user: stored ? JSON.parse(stored) : null,
  token: localStorage.getItem('erm-token'),
  org: storedOrg ? JSON.parse(storedOrg) : null,
  allOrgs: storedAllOrgs ? JSON.parse(storedAllOrgs) : [],
  viewAsUser: false,
  originalToken: localStorage.getItem('erm-original-token'),
  originalUser: localStorage.getItem('erm-original-user')
    ? JSON.parse(localStorage.getItem('erm-original-user')!)
    : null,
  isImpersonating: !!localStorage.getItem('erm-original-token'),

  setAuth: (user, token, org = null, allOrgs = []) => {
    localStorage.setItem('erm-token', token);
    localStorage.setItem('erm-user', JSON.stringify(user));
    if (org) localStorage.setItem('erm-org', JSON.stringify(org));
    else localStorage.removeItem('erm-org');
    localStorage.setItem('erm-all-orgs', JSON.stringify(allOrgs));
    set({ user, token, org, allOrgs, viewAsUser: false });
  },

  // Silent access-token renewal (sessionManager refresh) — same login, same
  // user/org, just a fresher short-lived token.
  setToken: (token) => {
    localStorage.setItem('erm-token', token);
    set({ token });
  },

  updateUser: (patch) => {
    const current = get().user;
    if (!current) return;
    const next = { ...current, ...patch };
    localStorage.setItem('erm-user', JSON.stringify(next));
    set({ user: next });
  },

  setOrg: (org, token) => {
    // Filters are per-organization scope — a persisted scope carrying another
    // org's ids across a switch would be a silent cross-tenant read (RULE 16).
    useFilterStore.getState().clearFilters();
    localStorage.setItem('erm-org', JSON.stringify(org));
    localStorage.setItem('erm-token', token);
    set({ org, token });
  },

  logout: () => {
    // Revoke the session server-side (kills the refresh cookie's power even if
    // a copy lingers) — fire-and-forget with raw axios so the api instance's
    // 401 interceptor can't loop back into logout. Expired/legacy tokens just
    // 401 here, which is fine: there's nothing live to revoke.
    const token = get().token;
    if (token) {
      axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    localStorage.removeItem('erm-token');
    localStorage.removeItem('erm-user');
    localStorage.removeItem('erm-org');
    localStorage.removeItem('erm-all-orgs');
    localStorage.removeItem('erm-original-token');
    localStorage.removeItem('erm-original-user');
    // The persisted filter scope ends with the session — the next login (any
    // user, this tab) starts unscoped, never on someone else's investigation.
    useFilterStore.getState().clearFilters();
    set({ user: null, token: null, org: null, allOrgs: [], viewAsUser: false, originalToken: null, originalUser: null, isImpersonating: false });
  },

  isAdmin: () => get().user?.role === 'admin' && !get().viewAsUser,
  isSuperAdmin: () => !!(get().user?.superAdmin && !get().isImpersonating),

  toggleViewAsUser: () => set(state => ({ viewAsUser: !state.viewAsUser })),

  startImpersonation: (user, token) => {
    const currentToken = get().token;
    const currentUser = get().user;
    localStorage.setItem('erm-original-token', currentToken!);
    localStorage.setItem('erm-original-user', JSON.stringify(currentUser));
    localStorage.setItem('erm-token', token);
    localStorage.setItem('erm-user', JSON.stringify(user));
    set({ user, token, isImpersonating: true, originalToken: currentToken, originalUser: currentUser });
  },

  exitImpersonation: () => {
    const originalToken = get().originalToken;
    const originalUser = get().originalUser;
    if (!originalToken || !originalUser) return;
    localStorage.setItem('erm-token', originalToken);
    localStorage.setItem('erm-user', JSON.stringify(originalUser));
    localStorage.removeItem('erm-original-token');
    localStorage.removeItem('erm-original-user');
    set({ user: originalUser, token: originalToken, isImpersonating: false, originalToken: null, originalUser: null });
  },
}));
